import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { reportsAPI, dataAPI } from '../../services/api';
import DateSelector from '../Common/DateSelector';
import RadiusSelector from '../Common/RadiusSelector';
import AccountMenu from '../Common/AccountMenu';
import KnowledgePanel from '../Common/KnowledgePanel';
import TabNavigation, { Tab } from '../Common/TabNavigation';
import ReportsView from './ReportsView';
import FaqView from './FaqView';
import CreativeView from './CreativeView';
import SportSignageView from './SportSignageView';
import { AllReportData, Distributor } from '../../types';
import { format, subDays } from 'date-fns';
import { getDefinition } from '../../data/advertisingDefinitions';
import AlertBanner from '../Common/AlertBanner';
import './DealerDashboard.css';

interface DealerDashboardProps {
  selectedDistributor?: Distributor;
  onBackToCorporate?: () => void;
  isCorporateView?: boolean;
}

const DealerDashboard: React.FC<DealerDashboardProps> = ({ 
  selectedDistributor, 
  onBackToCorporate, 
  isCorporateView = false 
}) => {
  const { user } = useAuth();
  const location = useLocation();
  const [reportData, setReportData] = useState<AllReportData | null>(null);
  const [dealerInfo, setDealerInfo] = useState<any>(null);
  const [dealerLocations, setDealerLocations] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const getDefaultStartDate = () => {
    // Campaign started September 1, 2025 - always use this as default start date
    return new Date(2025, 8, 1); // September 1, 2025 (month 8 = September)
  };

  const [dateRange, setDateRange] = useState({
    startDate: getDefaultStartDate(),
    endDate: new Date()
  });
  const [selectedRadius, setSelectedRadius] = useState<number>(30); // Default to 30 miles
  const [knowledgePanelOpen, setKnowledgePanelOpen] = useState(false);
  const [currentTopic, setCurrentTopic] = useState<string>('');
  const [activeTab, setActiveTab] = useState<string>('reports');
  const [mlbSocialData, setMlbSocialData] = useState<{ views: number; engagement: number } | null>(null);

  useEffect(() => {
    fetchDealerInfo();
    fetchReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, selectedRadius, user, selectedDistributor, location.state]);

  const fetchDealerInfo = async () => {
    // First check if we have dealer data from navigation state (highest priority)
    const locationState = location.state as any;
    if (locationState?.selectedDistributor) {
      setDealerInfo({
        id: locationState.selectedDistributor.distributor_id,
        name: locationState.selectedDistributor.name,
        address: locationState.selectedDistributor.address || '',
        city: locationState.selectedDistributor.city || '',
        state: locationState.selectedDistributor.state || '',
        zip: locationState.selectedDistributor.zip || '',
        email: locationState.selectedDistributor.email || ''
      });
      return;
    }
    
    // If we have a selected distributor from corporate view, use that
    if (selectedDistributor) {
      setDealerInfo({
        id: selectedDistributor.distributor_id,
        name: selectedDistributor.name,
        address: selectedDistributor.address || '',
        city: selectedDistributor.city || '',
        state: selectedDistributor.state || '',
        zip: selectedDistributor.zip || '',
        email: selectedDistributor.email || ''
      });
      return;
    }
    
    // Otherwise, fetch based on logged-in user
    if (!user) return;
    
    try {
      // Always try to fetch the full distributor info first if we have a distributor ID
      let distributorId: number | undefined;
      
      // Try multiple ways to get the distributor ID
      if (user.distributor) {
        distributorId = parseInt(user.distributor);
      } else if (user.distributor_id) {
        distributorId = user.distributor_id;
      }
      
      if (distributorId && !isNaN(distributorId)) {
        try {
          const response = await dataAPI.getDistributor(distributorId);
          if (response.success && response.data && response.data.address) {
            // Successfully got complete distributor data with address
            setDealerInfo({
              id: response.data.distributor_id,
              name: response.data.name,
              address: response.data.address,
              city: response.data.city,
              state: response.data.state,
              zip: response.data.zip,
              email: response.data.email
            });
            return;
          }
        } catch (apiErr) {
          console.error('API call failed, falling back to user data:', apiErr);
        }
      }
      
      // Fallback to user object fields
      setDealerInfo({
        id: user.distributor_id || user.id,
        name: user.dealer_name || user.name || user.username || 'Dealer',
        address: user.address || '',
        city: user.city || '',
        state: user.state || '',
        zip: user.zip || ''
      });
    } catch (err) {
      console.error('Failed to fetch dealer info:', err);
      // Final fallback to user object fields on error
      setDealerInfo({
        id: user.distributor_id || user.id,
        name: user.dealer_name || user.name || user.username || 'Dealer',
        address: user.address || '',
        city: user.city || '',
        state: user.state || '',
        zip: user.zip || ''
      });
    }
  };

  const fetchReportData = async () => {
    if (!user && !selectedDistributor) return;

    setIsLoading(true);
    setError(null);

    try {
      // Get dealer identifier for MongoDB query (use dealer_id - the unique distributor ID)
      let dealerId: string | undefined;

      // Check navigation state first (highest priority)
      const locationState = location.state as any;
      if (locationState?.selectedDistributor) {
        // Prefer dealer_id (the string like "78903549") over name
        // Convert distributor_id to string if it's a number
        const distId = locationState.selectedDistributor.dealer_id ||
                       locationState.selectedDistributor.distributor_id;
        dealerId = distId ? String(distId) : locationState.selectedDistributor.name;
        console.log('Using dealer from navigation state:', dealerId);
      } else if (selectedDistributor) {
        // Convert distributor_id to string if it's a number
        dealerId = selectedDistributor.distributor_id
          ? String(selectedDistributor.distributor_id)
          : selectedDistributor.name;
        console.log('Using dealer from selectedDistributor prop:', dealerId);
      } else if (user) {
        dealerId = user.distributor || user.dealer_id || user.name;
        console.log('Using dealer from user:', dealerId);
      }

      console.log('[OPTIMIZATION #2] Dealer: Using consolidated endpoint');
      console.log('[DealerDashboard] Calling consolidated endpoint with dealerId:', dealerId);
      const consolidatedStart = performance.now();

      // OPTIMIZATION #2: Single consolidated API call replaces 4 separate calls
      const [consolidated, socialData] = await Promise.all([
        reportsAPI.getConsolidatedDashboard(
          dealerId,
          format(dateRange.startDate, 'yyyy-MM-dd'),
          format(dateRange.endDate, 'yyyy-MM-dd'),
          selectedRadius
        ),
        reportsAPI.getMlbSocialSummary(
          format(dateRange.startDate, 'yyyy-MM-dd'),
          format(dateRange.endDate, 'yyyy-MM-dd')
        ).catch(() => null)
      ]);
      if (socialData) setMlbSocialData({ views: socialData.views || 0, engagement: socialData.engagement || 0 });

      const consolidatedEnd = performance.now();
      console.log(`[OPTIMIZATION #2] Dealer consolidated endpoint took ${(consolidatedEnd - consolidatedStart).toFixed(2)}ms`);

      // Extract data from consolidated response
      const response = consolidated.summary;
      const charts = consolidated.charts;
      const mapResponse = consolidated.map;
      const spotsData = consolidated.spots;

      // MongoDB v2 API returns: { role, dealer_name, date_range, channels }
      // channels format: { "Display": { impressions, clicks, records }, ... }
      if (response.channels) {
        // Convert MongoDB channels format to legacy AllReportData format
        const channels = response.channels;
        const summary = {
          display: {
            impressions: channels.Display?.impressions || 0,
            clicks: channels.Display?.clicks || 0
          },
          dooh: {
            impressions: channels.DOOH?.impressions || 0,
            clicks: channels.DOOH?.clicks || 0
          },
          streaming_tv: {
            impressions: channels['Streaming TV']?.impressions || 0,
            clicks: channels['Streaming TV']?.clicks || 0
          },
          broadcast_tv: {
            impressions: channels['Broadcast TV']?.impressions || 0,
            clicks: channels['Broadcast TV']?.clicks || 0
          },
          streaming_audio: {
            impressions: channels['Streaming Audio']?.impressions || 0,
            clicks: channels['Streaming Audio']?.clicks || 0
          },
          radio: {
            impressions: channels['Radio']?.impressions || 0,
            clicks: channels['Radio']?.clicks || 0
          },
          sport_signage: {
            impressions: channels['Sport Signage']?.impressions || 0,
            clicks: 0
          },
          youtube: {
            impressions: channels.YouTube?.impressions || 0,
            clicks: channels.YouTube?.clicks || 0
          },
          social: {
            impressions: channels.Social?.impressions || 0,
            clicks: channels.Social?.clicks || 0
          },
          paid_social: {
            impressions: channels['Paid Social']?.impressions || 0,
            clicks: channels['Paid Social']?.clicks || 0
          },
          store_visits: channels['Store Visits']?.impressions || 0
        };

        // Transform map data for MapView component
        const mapData: any = { display: [], dooh: [], streaming_tv: [] };

        if (mapResponse.zip_codes && Array.isArray(mapResponse.zip_codes)) {
          mapResponse.zip_codes.forEach((zipData: any) => {
            // Extract coordinates from backend response
            const coordinates: [number, number] | undefined =
              (zipData.longitude && zipData.latitude)
                ? [zipData.longitude, zipData.latitude]
                : undefined;

            // Process each channel within this ZIP code
            if (zipData.channels && Array.isArray(zipData.channels)) {
              zipData.channels.forEach((channelData: any) => {
                const channelKey = channelData.channel.toLowerCase().replace(' ', '_');

                if (mapData[channelKey]) {
                  mapData[channelKey].push({
                    zip: zipData.zip_code,
                    impressions: channelData.impressions || 0,
                    coordinates: coordinates,
                    channels: zipData.channels,  // Include ALL channels for popup
                    city: zipData.city,
                    state: zipData.state
                  });
                }
              });
            }
          });
        }

        console.log('Transformed map data:', mapData);
        console.log('Display channel ZIP count:', mapData.display?.length || 0);

        setReportData({
          summary,
          display_chart: charts.Display || [],
          dooh_chart: charts.DOOH || [],
          streaming_tv_chart: charts['Streaming TV'] || [],
          broadcast_tv_chart: charts['Broadcast TV'] || [],
          streaming_audio_chart: charts['Streaming Audio'] || [],
          radio_chart: charts.Radio || [],
          youtube_chart: charts.YouTube || [],
          search_chart: charts.Search || [],
          social_chart: charts.Social || [],
          paid_social_chart: charts['Paid Social'] || [],
          store_visits_chart: [],
          sport_signage_chart: charts['Sport Signage'] || [],
          sport_signage: spotsData.sport_signage || [],
          radio: spotsData.radio || [],
          television: spotsData.television || [],
          map: mapData
        });

        // Set dealer location for map radius circles
        if (mapResponse.dealer_location) {
          const dealerLoc = mapResponse.dealer_location;
          setDealerLocations([{
            dealer_id: dealerLoc.dealer_id,
            name: dealerLoc.dealer_name,
            latitude: dealerLoc.latitude,
            longitude: dealerLoc.longitude,
            address: dealerLoc.address,
            city: dealerLoc.city,
            state: dealerLoc.state,
            zip: dealerLoc.zip
          }]);
        } else {
          setDealerLocations([]);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load report data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDateChange = (startDate: Date, endDate: Date) => {
    setDateRange({ startDate, endDate });
  };

  const handleRadiusChange = (radius: number) => {
    setSelectedRadius(radius);
  };

  const handleHelpClick = (topic: string) => {
    if (knowledgePanelOpen && currentTopic === topic) {
      setKnowledgePanelOpen(false);
    } else {
      setCurrentTopic(topic);
      setKnowledgePanelOpen(true);
    }
  };

  const handleKnowledgePanelClose = () => {
    setKnowledgePanelOpen(false);
  };

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    // Close knowledge panel when switching tabs
    setKnowledgePanelOpen(false);
  };

  if (isLoading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading report data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <p>Error: {error}</p>
        <button onClick={fetchReportData}>Retry</button>
      </div>
    );
  }

  if (!reportData) {
    return <div className="dashboard-error">No data available</div>;
  }

  const tabs: Tab[] = [
    {
      id: 'reports',
      label: 'Reports',
      icon: 'analytics',
      content: (
        <ReportsView
          reportData={reportData}
          dealerInfo={dealerInfo}
          dealerLocations={dealerLocations}
          selectedRadius={selectedRadius}
          onHelpClick={handleHelpClick}
          isLoading={isLoading}
          mlbSocialData={mlbSocialData}
        />
      )
    },
    {
      id: 'faqs',
      label: 'FAQs',
      icon: 'help',
      content: <FaqView />
    },
    {
      id: 'creative',
      label: 'Creative',
      icon: 'palette',
      content: <CreativeView />
    },
    {
      id: 'sport-signage',
      label: 'Sport Signage',
      icon: 'stadium',
      content: <SportSignageView />
    }
  ];

  return (
    <div className="dashboard-container dealer-dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-left">
            <div className="header-logos">
              <img src="/img/gravely_new.svg" alt="Gravely" className="header-logo gravely-logo" />
              <img src="/img/ariens.svg" alt="Ariens" className="header-logo ariens-logo" />
            </div>
            <div className="dealer-info">
              <h1>{dealerInfo?.name || user?.dealer_name || user?.name || `Dealer ${user?.distributor || user?.distributor_id || ''}`}</h1>
              {dealerInfo && (dealerInfo.address || dealerInfo.city || dealerInfo.state || dealerInfo.zip) ? (
                <p>
                  {[dealerInfo.address, dealerInfo.city, dealerInfo.state, dealerInfo.zip]
                    .filter(Boolean)
                    .join(', ')}
                </p>
              ) : (
                <p>{[user?.address, user?.city, user?.state, user?.zip].filter(Boolean).join(', ') || `ID: ${user?.distributor || user?.distributor_id || user?.id}`}</p>
              )}
            </div>
          </div>
          <div className="header-right">
            {isCorporateView && onBackToCorporate && (
              <button className="back-to-corporate-btn" onClick={onBackToCorporate}>
                <i className="material-icons">arrow_back</i>
                <span>Back to Corporate View</span>
              </button>
            )}
            <RadiusSelector
              selectedRadius={selectedRadius}
              onRadiusChange={handleRadiusChange}
              disabled={isLoading}
            />
            <DateSelector
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onDateChange={handleDateChange}
            />
            <AccountMenu />
          </div>
        </div>
      </header>

      <AlertBanner />

      <main className="dashboard-content">
        <TabNavigation
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
      </main>

      {/* Knowledge Panel */}
      <KnowledgePanel
        isOpen={knowledgePanelOpen}
        onClose={handleKnowledgePanelClose}
        topic={currentTopic}
        title={getDefinition(currentTopic)?.title || ''}
        description={getDefinition(currentTopic)?.description || ''}
      />
    </div>
  );
};

export default DealerDashboard;
